import { Handle, Position } from "@xyflow/react";
import CallSplitIcon from '@mui/icons-material/CallSplit';
import { Box, Typography, useTheme } from "@mui/material";
import ReusableNodes, { NodeError, NodeHandles, NodeHeader, NodeWarning } from "../../../../../components/ReusableNodes";
import { CustomTooltip } from "../../../../../components/TooltipCustomStyle";
import TooltipStatusTitle from "../../../../../components/TooltipStatusTitle";
import ErrorIcon from '@mui/icons-material/Error';
import CheckHandleNodeEvent from "../../../../../utils/CheckHandleNodeEvent";
import DrgIcon from "../../drgicons/DrgIcon.tsx";
import isSelectedAndHighlight from "../../../../../utils/isSelectedAndHighlight.js";

export default function CustomConjunctionNode({ data, selected, id, onNodeClick }) {

    const theme = useTheme();

    const label = data.general_properties.label;
    const description = data.general_properties.description;

    const handleNodeClick = (event) => {
        event.stopPropagation(); // ป้องกัน event bubbling
        if (onNodeClick) {
            onNodeClick(event, id);
        }
    };

    const nodeHandles = ({ headerAnchorY } = {}) => {
        const handleClassName = `drg-handle ${selected ? 'drg-selected-handle' : ''}`;

        const sideHandleStyle = typeof headerAnchorY === 'number'
            ? { top: `${headerAnchorY}px` }
            : undefined;

        return (
            <div>
                <Handle
                    id="top-target"
                    type="target"
                    position={Position.Top}
                    className={handleClassName}
                />
                <Handle
                    id="top-source"
                    type="source"
                    position={Position.Top}
                    className={handleClassName}
                />
                <Handle
                    id="bottom-target"
                    type="target"
                    position={Position.Bottom}
                    className={handleClassName}
                />
                <Handle
                    id="bottom-source"
                    type="source"
                    position={Position.Bottom}
                    className={handleClassName}
                />
                <Handle
                    id="left-target"
                    type="target"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="left-source"
                    type="source"
                    position={Position.Left}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-target"
                    type="target"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
                <Handle
                    id="right-source"
                    type="source"
                    position={Position.Right}
                    style={sideHandleStyle}
                    className={handleClassName}
                />
            </div>
        )
    };

    return (
        <ReusableNodes
            selected={selected}
            highlighted={data?.highlighted}
            focusMode={data?.focusMode}
            isContentExpandable={false}
            backgroundColor="#ffffff"
            onClick={handleNodeClick}
        >
            {data?.warning === true && (
                <NodeWarning>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Warning" listDetails={data?.warning_text} />} arrow placement="top" tooltipcolorstyle={theme.palette.warning.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.warning.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeWarning>
            )}
            {(data?.error === true && data?.warning === false || data?.error_text.length > 0) && (
                <NodeError>
                    <CustomTooltip title={<TooltipStatusTitle headerTitle="Error" listDetails={[...new Set([...(data?.error_dpe_text || []), ...(data?.error_text || [])])]} />} arrow placement="top" tooltipcolorstyle={theme.palette.error.main}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: '50%',
                                background: '#fff',
                                width: 15,
                                height: 15,
                                marginTop: '5px'
                            }}
                        >
                            <ErrorIcon sx={{
                                color: theme.palette.error.main,
                                fontSize: 18,
                            }} />
                        </Box>
                    </CustomTooltip>
                </NodeError>
            )}
            <NodeHeader>
                <div className="drg-icon bg-gray-100">
                    <svg
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        fill="none"
                        preserveAspectRatio="xMidYMid meet"
                        className="block h-7 w-7"
                    >
                        <path d="M4.0772 12H8.3803M15.6197 12H19.9228M14.8893 12C14.8893 13.5976 13.5941 14.8928 11.9964 14.8928C10.3987 14.8928 9.1036 13.5976 9.1036 12C9.1036 10.4023 10.3987 9.1071 11.9964 9.1071C13.5941 9.1071 14.8893 10.4023 14.8893 12Z" stroke="#3B67E2" style={{ strokeWidth: 1.2295 }} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
                <div className="drg-label">
                    <Typography variant="body2" className="text-gray-900" sx={{ fontWeight: 700 }}>
                        {label}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: 14 }}>
                        {description}
                    </Typography>
                </div>
            </NodeHeader>
            <NodeHandles>
                {nodeHandles}
            </NodeHandles>
        </ReusableNodes>
    );
}